//car create dialog box
import { createCARRecord } from "../../../utils/createCARRecord";
import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import {
  Dialog,
  DialogContent,
  Typography,
  TextField,
  FormControlLabel,
  Checkbox,
  Box,
  Autocomplete,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Stack,
  Tooltip,
  Fade,
} from "@mui/material";
import {
  fetchCRMAccounts,
  filterAccountsBySearchTerm,
  registerAccountsLoadingHandler,
  searchAccountsByName,
} from "../../../api/CRMAccountsData";
import BackWithArrow from "../../common/BackWithArrow";
import { fetchEmployees } from "../../../api/employeeUtils"; // Updated import path
import GlobalButton from "../../common/GlobalButton";
import SaveIcon from "@mui/icons-material/Save";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import { fetchSONames, filterSOsBySearchTerm } from "../../../api/FetchWOName";
//
import Loader from "../../common/Loader";
import { fetchMachines } from "../../../api/GETallMachines";
//
export default function CARCreateDialog({
  open,
  onClose,
  formData,
  handleInputChange,
  fetchCARData,
}) {
  // Add this with your other state declarations
  const [isSubmitting, setIsSubmitting] = useState(false);

  // CRM Accounts data state
  const [accounts, setAccounts] = useState([]);
  const [filteredAccounts, setFilteredAccounts] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [isLoadingAccounts, setIsLoadingAccounts] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [searchTimeout, setSearchTimeout] = useState(null);

  useEffect(() => {
    const unregister = registerAccountsLoadingHandler((isLoading) => {
      setIsLoadingAccounts(isLoading);
    });

    const loadAccounts = async () => {
      try {
        const initialAccounts = await fetchCRMAccounts();
        setAccounts(initialAccounts);
        setFilteredAccounts(initialAccounts);
      } catch (error) {
        console.error("Error loading accounts:", error);
      } finally {
        setLoading(false);
      }
    };
    loadAccounts();
    return () => {
      unregister();
      if (searchTimeout) clearTimeout(searchTimeout);
    };
  }, []);

  const debouncedSearch = useCallback(
    (term) => {
      if (searchTimeout) {
        clearTimeout(searchTimeout);
      }

      if (!term || term.length < 2) {
        setFilteredAccounts(accounts);
        return;
      }

      const timeoutId = setTimeout(async () => {
        setIsLoadingAccounts(true);
        try {
          const results = await searchAccountsByName(term);
          setFilteredAccounts(results);
        } catch (error) {
          console.error("Error searching accounts:", error);
          const filtered = filterAccountsBySearchTerm(accounts, term);
          setFilteredAccounts(filtered);
        }
      }, 500);

      setSearchTimeout(timeoutId);
    },
    [accounts, searchTimeout]
  );

  const handleSearchChange = (event) => {
    const term = event.target.value;
    setSearchTerm(term);
    debouncedSearch(term);
  };

  const handleAccountChange = (event, newValue) => {
    setSelectedAccount(newValue);

    // Store account ID in window object for global access
    if (newValue && newValue.id) {
      window.selectedAccountId = newValue.id;
      window.selectedAccountName = newValue.name || "";
      console.log(
        "Account selected globally:",
        window.selectedAccountId,
        window.selectedAccountName
      );
    } else {
      window.selectedAccountId = null;
      window.selectedAccountName = "";
    }
  };

  const handleInputChangeAccount = (event, newInputValue) => {
    setInputValue(newInputValue);
  };

  // Machines data state
  const [loading, setLoading] = useState(true);
  const [machines, setMachines] = useState([]);
  const [selectedMachine, setSelectedMachine] = useState("");

  // Load machines data
  useEffect(() => {
    const loadMachines = async () => {
      setLoading(true);
      try {
        const machinesData = await fetchMachines();
        setMachines(machinesData);
      } catch (error) {
        console.error("Error loading machines:", error);
      } finally {
        setLoading(false);
      }
    };

    loadMachines();
  }, []);

  // Handle machine selection
  const handleMachineChange = (event) => {
    const selectedValue = event.target.value;
    setSelectedMachine(selectedValue);

    // Find the selected machine to log details
    const machine = machines.find((m) => m.id === selectedValue);
    if (machine) {
      console.log(`Selected Machine: ${machine.name} (ID: ${machine.id})`);
    }
  };

  // add states for SO name
  const [soData, setSOData] = useState([]);
  const [loadingSO, setLoadingSO] = useState(false);
  //const [loading, setLoading] = useState(true);
  const [searchTermso, setSearchTermso] = useState("");
  const [selectedSO, setSelectedSO] = useState(null);
  //const [message, setMessage] = useState(null);
  const [showTooltip, setShowTooltip] = useState(false);
  const autocompleteRef = useRef(null);
  //const tooltipTimeoutRef = useRef(null);

  // Fetch SO data on component mount
  useEffect(() => {
    const loadSOData = async () => {
      setLoadingSO(true);
      try {
        const data = await fetchSONames();
        setSOData(data);
      } catch (error) {
        console.error("Error loading SO data:", error);
      } finally {
        setLoadingSO(false);
        setLoading(false);
      }
    };

    loadSOData();
  }, []);

  // Filter SO data based on search term
  const filteredSOData = useMemo(() => {
    return filterSOsBySearchTerm(soData, searchTermso);
  }, [soData, searchTermso]);

  // Handle selection of an SO
  const handleSOSelection = (event, value) => {
    setSelectedSO(value);

    if (value) {
      // Just log the essential data we need
      console.log("Selected SO Name:", value.name);
      console.log("Selected SO ID:", value.id);
      console.log("Selected SO Number:", value.number);
    } else {
      // setMessage(null);
      // setShowTooltip(false);
    }
  };

  // Handle creating a record with the selected SO ID
  const handleCreateRecord = () => {
    // Only use the ID and name for record creation
    const soId = selectedSO.id;
    const soName = selectedSO.name;
    console.log("Creating record with SO ID:", soId);
    console.log("SO Name:", soName);
    setMessage({
      type: "info",
      text: `Processing: ID=${soId}, Name=${soName}`,
    });
  };
  // Custom function to display the selected value in the input
  const getOptionLabel = (option) => {
    // For dropdown options, show full details
    if (option && option.name) {
      return option.name; // Only show name for the selected value in input field
    }
    return "";
  };
  //above code is for SO name
  //
  // below code line is for save button
  const [submitting, setSubmitting] = useState(false);
  //
  // Added states for employee data
  const [employees, setEmployees] = useState([]);
  const [qualityOwner, setQualityOwner] = useState("");
  const [qualityOwner1, setQualityOwner1] = useState("");
  const [error, setError] = useState({});
  //

  const handleSubmit = async () => {
    try {
      // Show the loader and set submitting state
      setIsSubmitting(true);
      setSubmitting(true);

      // Add quality owner to form data before submission
      const updatedFormData = {
        ...formData,
        qualityOwner: qualityOwner,
        qualityOwner1: qualityOwner1,
        // Also include account information if available
        accountId: window.selectedAccountId || "",
        soIdPalyoad: selectedSO?.id || "",
        MachineID: selectedMachine,
      };
      const response = await createCARRecord(updatedFormData);
      if (response.code === "SUCCESS") {
        // Reset specific fields directly
        handleInputChange("name", "");
        handleInputChange("date", "");
        handleInputChange("type", "");
        handleInputChange("Source", "");
        handleInputChange("Process", "");
        handleInputChange("Priority", "");
        handleInputChange("assignedTo", "");
        handleInputChange("AssignedDate", "");
        handleInputChange("respondBy", "");
        handleInputChange("containment", "");
        handleInputChange("rootCause", "");
        handleInputChange("actionPlan", "");
        handleInputChange("actionCompleted", "");
        handleInputChange("Assignee_Name", "");
        handleInputChange("verifiedDate", "");
        handleInputChange("notes", "");
        handleInputChange("Describe_the_issue_or_problem_in_detail", "");
        setQualityOwner("");
        setQualityOwner1("");
        setSelectedAccount(null);
        setSelectedSO(null);
        setSelectedMachine("");

        // Reset all other fields
        const initialState = {
          name: "",
          date: "",
          job: "",
          type: "",
          Source: "",
          Process: "",
          Priority: "",
          accountId: "",
          qualityOwner: "",
          soIdPalyoad: "",
          qualityOwner1: "",
          MachineID: "",
          Reported_By: "",
          Describe_the_issue_or_problem_in_detail: "",
          assignedTo: "",
          AssignedDate: "",
          respondBy: "",
          containment: "",
          rootCause: "",
          actionPlan: "",
          actionCompleted: "",
          Assignee_Name: "",
          verifiedDate: "",
          notes: "",
        };

        // Apply all resets
        Object.entries(initialState).forEach(([field, value]) => {
          handleInputChange(field, value);
        });
        await fetchCARData(); // Refresh the cards
        onClose(); // ✅ close the dialog
      } else {
        alert("Failed to create record: " + response.message);
      }
    } catch (error) {
      alert("An error occurred while creating the record.");
    } finally {
      // Hide the loader and reset submitting state
      setIsSubmitting(false);
      setSubmitting(false);
    }
  };
  // Fetch employees when the component mounts
  useEffect(() => {
    const loadData = async () => {
      try {
        // Fetch employees
        const employeeData = await fetchEmployees();
        console.log("Employees loaded:", employeeData);
        setEmployees(employeeData || []);
      } catch (error) {
        console.error("Failed to load data:", error);
        setIsLoadingAccounts(false);
      }
    };

    loadData();
  }, []);
  // Find the selected employee to log their details
  const selectedEmployee = employees.find(
    (emp) => emp.ID === qualityOwner || emp.ID === qualityOwner1
  );
  console.log(
    "Selected employee:",
    selectedEmployee ? selectedEmployee.Name.zc_display_value : "None",
    "with ID:",
    qualityOwner
  );
  //
  console.log(
    "Selected Verified By:",
    selectedEmployee ? selectedEmployee.Name.zc_display_value : "None",
    "with ID:",
    qualityOwner1
  );

  return (
    <Dialog
      open={open}
      fullScreen
      PaperProps={{
        sx: {
          minHeight: "100vh",
          maxHeight: "100vh",
          background: "white", // Remove background
        },
      }}
    >
      <BackWithArrow onBack={onClose} />
      <DialogContent dividers sx={{ maxHeight: "100vh", overflowY: "auto" }}>
        <Box display="grid" gap={2}>
          <Box
            display="grid"
            gridTemplateColumns="repeat(auto-fit, minmax(250px, 1fr))"
            gap={2}
          >
            <TextField
              sx={{
                // Input text color - always black
                "& .MuiInputBase-input": {
                  color: "#000000", // Black text color
                },
                // Border styling
                "& .MuiOutlinedInput-root": {
                  // Default state - light gray
                  "& fieldset": {
                    borderColor: "#d3d3d3", // Light gray as default
                  },
                  // Focus state
                  "&.Mui-focused fieldset": {
                    borderColor: "#4F46E5", // Keep consistent with your desired color
                  },
                },
                // Label color
                "& .MuiInputLabel-root.Mui-focused": {
                  color: "#4F46E5", // Changes label color to match when focused
                },
              }}
              label="CAR Name"
              size="small"
              fullWidth
              value={formData.name || ""}
              onChange={(e) => handleInputChange("name", e.target.value)}
            />
            <TextField
              sx={{
                // Input text color - always black
                "& .MuiInputBase-input": {
                  color: "#000000", // Black text color
                },
                // Border styling
                "& .MuiOutlinedInput-root": {
                  // Default state - light gray
                  "& fieldset": {
                    borderColor: "#d3d3d3", // Light gray as default
                  },
                  // Focus state
                  "&.Mui-focused fieldset": {
                    borderColor: "#4F46E5", // Keep consistent with your desired color
                  },
                },
                // Label color
                "& .MuiInputLabel-root.Mui-focused": {
                  color: "#4F46E5", // Changes label color to match when focused
                },
              }}
              type="date"
              label="Date"
              size="small"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={formData.date || ""}
              onChange={(e) => handleInputChange("date", e.target.value)}
            />

            <Autocomplete
              sx={{
                "& .MuiInputBase-input": {
                  color: "#000000",
                },
                "& .MuiOutlinedInput-root": {
                  "& fieldset": {
                    borderColor: "#d3d3d3",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "#4F46E5",
                  },
                },
                "& .MuiInputLabel-root.Mui-focused": {
                  color: "#4F46E5",
                },
              }}
              id="account-search"
              options={filteredAccounts}
              getOptionLabel={(option) => option.name || ""}
              onChange={handleAccountChange}
              onInputChange={handleInputChangeAccount}
              inputValue={inputValue}
              value={selectedAccount}
              loading={isLoadingAccounts}
              fullWidth
              filterOptions={(options, state) => options}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Search Customer Name"
                  size="small"
                  variant="outlined"
                  onChange={handleSearchChange}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {isLoadingAccounts ? (
                          <CircularProgress color="inherit" size={20} />
                        ) : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          </Box>
          {/* Add Account Search Dropdown */}
          <Box
            display="grid"
            gridTemplateColumns="repeat(auto-fit, minmax(250px, 1fr))"
            gap={2}
          >
            <FormControl
              sx={{
                // Input text color - always black
                "& .MuiInputBase-input": {
                  color: "#000000", // Black text color
                },
                // Border styling
                "& .MuiOutlinedInput-root": {
                  // Default state - light gray
                  "& fieldset": {
                    borderColor: "#d3d3d3", // Light gray as default
                  },
                  // Focus state
                  "&.Mui-focused fieldset": {
                    borderColor: "#4F46E5", // Keep consistent with your desired color
                  },
                },
                // Label color
                "& .MuiInputLabel-root.Mui-focused": {
                  color: "#4F46E5", // Changes label color to match when focused
                },
              }}
              fullWidth
              size="small"
              error={!!error.qualityOwner}
            >
              <InputLabel>Employee</InputLabel>
              <Select
                value={qualityOwner || ""}
                onChange={(e) => {
                  const value = e.target.value;
                  console.log("Selected quality owner ID:", value);
                  setQualityOwner(value);
                  // Also update the main form data
                  handleInputChange("qualityOwner", value);
                }}
                label="Quality Owner"
              >
                <MenuItem value="" disabled>
                  Select User
                </MenuItem>
                {employees && employees.length > 0 ? (
                  employees.map((employee) => (
                    <MenuItem key={employee.ID} value={employee.ID}>
                      {employee.Name.zc_display_value}
                    </MenuItem>
                  ))
                ) : (
                  <MenuItem value="" disabled>
                    No User Found
                  </MenuItem>
                )}
              </Select>
              {error.qualityOwner && (
                <Typography variant="caption" color="error">
                  {error.qualityOwner}
                </Typography>
              )}
            </FormControl>

            <FormControl
              sx={{
                // Input text color - always black
                "& .MuiInputBase-input": {
                  color: "#000000", // Black text color
                },
                // Border styling
                "& .MuiOutlinedInput-root": {
                  // Default state - light gray
                  "& fieldset": {
                    borderColor: "#d3d3d3", // Light gray as default
                  },
                  // Focus state
                  "&.Mui-focused fieldset": {
                    borderColor: "#4F46E5", // Keep consistent with your desired color
                  },
                },
                // Label color
                "& .MuiInputLabel-root.Mui-focused": {
                  color: "#4F46E5", // Changes label color to match when focused
                },
              }}
              fullWidth
              size="small"
            >
              <InputLabel id="lookup-label">CAR Reported By</InputLabel>
              <Select
                labelId="lookup-label"
                id="lookup-select"
                value={formData.Reported_By || ""}
                label="CAR Reported By"
                onChange={(e) =>
                  handleInputChange("Reported_By", e.target.value)
                }
              >
                <MenuItem value="Reported by customer">
                  Reported by customer
                </MenuItem>
                <MenuItem value="Reported internally">
                  Reported internally
                </MenuItem>
              </Select>
            </FormControl>

            <FormControl
              sx={{
                // Input text color - always black
                "& .MuiInputBase-input": {
                  color: "#000000", // Black text color
                },
                // Border styling
                "& .MuiOutlinedInput-root": {
                  // Default state - light gray
                  "& fieldset": {
                    borderColor: "#d3d3d3", // Light gray as default
                  },
                  // Focus state
                  "&.Mui-focused fieldset": {
                    borderColor: "#4F46E5", // Keep consistent with your desired color
                  },
                },
                // Label color
                "& .MuiInputLabel-root.Mui-focused": {
                  color: "#4F46E5", // Changes label color to match when focused
                },
              }}
              fullWidth
              size="small"
            >
              <InputLabel id="machine-select-label">Select Machine</InputLabel>
              <Select
                labelId="machine-select-label"
                id="machine-select"
                value={selectedMachine}
                label="Select Machine"
                onChange={handleMachineChange}
              >
                {machines.map((machine) => (
                  <MenuItem key={machine.id} value={machine.id}>
                    {machine.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          <Box
            display="grid"
            gap={2}
            sx={{ minWidth: "440px", maxWidth: "250px" }}
          >
            {/* below code is for so name (job name) */}
            <Tooltip
              fullWidth
              size="small"
              open={showTooltip && selectedSO !== null}
              placement="top-start"
              arrow
              TransitionComponent={Fade}
              TransitionProps={{ timeout: 600 }}
              disableFocusListener
              disableHoverListener
              disableTouchListener
            >
              <Autocomplete
                sx={{
                  // Input text color - always black
                  "& .MuiInputBase-input": {
                    color: "#000000", // Black text color
                  },
                  // Border styling
                  "& .MuiOutlinedInput-root": {
                    // Default state - light gray
                    "& fieldset": {
                      borderColor: "#d3d3d3", // Light gray as default
                    },
                    // Focus state
                    "&.Mui-focused fieldset": {
                      borderColor: "#4F46E5", // Keep consistent with your desired color
                    },
                  },
                  // Label color
                  "& .MuiInputLabel-root.Mui-focused": {
                    color: "#4F46E5", // Changes label color to match when focused
                  },
                }}
                ref={autocompleteRef}
                id="so-search"
                options={filteredSOData}
                getOptionLabel={getOptionLabel}
                // For searching, still use the full string with number and account
                filterOptions={(options, state) => {
                  return filterSOsBySearchTerm(options, state.inputValue);
                }}
                // Only show name when selected in the input field
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Search Job name From Work Order"
                    variant="outlined"
                    fullWidth
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loadingSO ? (
                            <CircularProgress color="inherit" size={10} />
                          ) : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                // Control what appears in the dropdown menu during search
                renderOption={(props, option) => (
                  <li {...props}>
                    <Stack direction="column" spacing={0.5}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Typography variant="body1" fontWeight="bold">
                          {option.number}
                        </Typography>
                      </Stack>
                      <Typography variant="body2">
                        Job Name: {option.name}
                      </Typography>
                    </Stack>
                  </li>
                )}
                loading={loadingSO}
                value={selectedSO}
                onChange={handleSOSelection}
                onInputChange={(event, newInputValue) => {
                  setSearchTermso(newInputValue);
                }}
              />
            </Tooltip>
            {/* above code is for SO name */}
          </Box>
          {/* Type Section Checkboxes */}
          <Box display="flex" flexGrow={1}>
            <Box mr={5.3}>
              <Typography fontWeight="bold" lineHeight={2}>
                Type:
              </Typography>
              <Typography variant="body2">Check One</Typography>
            </Box>
            <Box
              display="grid"
              gridTemplateColumns="repeat(4, 1fr)"
              flexGrow={1}
            >
              <Box display="flex" flexDirection="column">
                {/* First column checkboxes */}
                <FormControlLabel
                  control={
                    <Checkbox
                      sx={{
                        color: "darkgray", // Dark gray when unchecked
                        "&.Mui-checked": {
                          color: "#4F46E5", // Indigo (#4F46E5) when checked
                        },
                      }}
                      checked={formData.type === "corrective"}
                      onChange={() =>
                        handleInputChange(
                          "type",
                          formData.type === "corrective" ? "" : "corrective"
                        )
                      }
                    />
                  }
                  label="Corrective action (existing issue)"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      sx={{
                        color: "darkgray", // Dark gray when unchecked
                        "&.Mui-checked": {
                          color: "#4F46E5", // Indigo (#4F46E5) when checked
                        },
                      }}
                      checked={formData.type === "improvement"}
                      onChange={() =>
                        handleInputChange(
                          "type",
                          formData.type === "improvement" ? "" : "improvement"
                        )
                      }
                    />
                  }
                  label="Opportunity for Improvement / suggestion"
                />
              </Box>
              <Box display="flex" flexDirection="column">
                {/* Second column checkboxes */}
                <FormControlLabel
                  control={
                    <Checkbox
                      sx={{
                        color: "darkgray", // Dark gray when unchecked
                        "&.Mui-checked": {
                          color: "#4F46E5", // Indigo (#4F46E5) when checked
                        },
                      }}
                      checked={formData.type === "preventive"}
                      onChange={() =>
                        handleInputChange(
                          "type",
                          formData.type === "preventive" ? "" : "preventive"
                        )
                      }
                    />
                  }
                  label="Preventive action (potential issue)"
                />
              </Box>
            </Box>
          </Box>
          {/* Source Section checkboxes  */}
          <Box display="flex" flexGrow={1}>
            <Box mr={4}>
              <Typography fontWeight="bold" lineHeight={2}>
                Source:
              </Typography>
              <Typography variant="body2">Check One</Typography>
            </Box>
            <Box
              display="grid"
              gridTemplateColumns="repeat(4, 1fr)"
              gap={2}
              flexGrow={1}
            >
              <Box display="flex" flexDirection="column">
                {/* First column checkboxes */}
                <FormControlLabel
                  control={
                    <Checkbox
                      sx={{
                        color: "darkgray", // Dark gray when unchecked
                        "&.Mui-checked": {
                          color: "#4F46E5", // Indigo (#4F46E5) when checked
                        },
                      }}
                      checked={formData.Source === "Employeecheckbox"}
                      onChange={() =>
                        handleInputChange(
                          "Source",
                          formData.Source === "Employeecheckbox"
                            ? ""
                            : "Employeecheckbox"
                        )
                      }
                    />
                  }
                  label="Employee"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      sx={{
                        color: "darkgray", // Dark gray when unchecked
                        "&.Mui-checked": {
                          color: "#4F46E5", // Indigo (#4F46E5) when checked
                        },
                      }}
                      checked={formData.Source === "CustomerSource"}
                      onChange={(e) =>
                        handleInputChange(
                          "Source",
                          formData.Source === "CustomerSource"
                            ? ""
                            : "CustomerSource"
                        )
                      }
                    />
                  }
                  label="Customer"
                />
              </Box>
              <Box display="flex" flexDirection="column">
                {/* Second column checkboxes */}
                <FormControlLabel
                  control={
                    <Checkbox
                      sx={{
                        color: "darkgray", // Dark gray when unchecked
                        "&.Mui-checked": {
                          color: "#4F46E5", // Indigo (#4F46E5) when checked
                        },
                      }}
                      checked={formData.Source === "ExternalAuditFinding"}
                      onChange={(e) =>
                        handleInputChange(
                          "Source",
                          formData.Source === "ExternalAuditFinding"
                            ? ""
                            : "ExternalAuditFinding"
                        )
                      }
                    />
                  }
                  label="External Audit Finding"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      sx={{
                        color: "darkgray", // Dark gray when unchecked
                        "&.Mui-checked": {
                          color: "#4F46E5", // Indigo (#4F46E5) when checked
                        },
                      }}
                      checked={formData.Source === "InternalAuditFinding"}
                      onChange={(e) =>
                        handleInputChange(
                          "Source",
                          formData.Source === "InternalAuditFinding"
                            ? ""
                            : "InternalAuditFinding"
                        )
                      }
                    />
                  }
                  label="Internal Audit Finding"
                />
              </Box>
              <Box display="flex" flexDirection="column">
                {/* Third column checkboxes */}
                <FormControlLabel
                  control={
                    <Checkbox
                      sx={{
                        color: "darkgray", // Dark gray when unchecked
                        "&.Mui-checked": {
                          color: "#4F46E5", // Indigo (#4F46E5) when checked
                        },
                      }}
                      checked={formData.Source === "SupplierSource"}
                      onChange={(e) =>
                        handleInputChange(
                          "Source",
                          formData.Source === "SupplierSource"
                            ? ""
                            : "SupplierSource"
                        )
                      }
                    />
                  }
                  label="Supplier / Subcontractor"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      sx={{
                        color: "darkgray", // Dark gray when unchecked
                        "&.Mui-checked": {
                          color: "#4F46E5", // Indigo (#4F46E5) when checked
                        },
                      }}
                      checked={formData.Source === "ManagementReview"}
                      onChange={(e) =>
                        handleInputChange(
                          "Source",
                          formData.Source === "ManagementReview"
                            ? ""
                            : "ManagementReview"
                        )
                      }
                    />
                  }
                  label="Management Review"
                />
              </Box>
              <Box display="flex" flexDirection="column">
                {/* Fourth column checkboxes */}
                <FormControlLabel
                  control={
                    <Checkbox
                      sx={{
                        color: "darkgray", // Dark gray when unchecked
                        "&.Mui-checked": {
                          color: "#4F46E5", // Indigo (#4F46E5) when checked
                        },
                      }}
                      checked={formData.Source === "OtherSource"}
                      onChange={(e) =>
                        handleInputChange(
                          "Source",
                          formData.Source === "OtherSource" ? "" : "OtherSource"
                        )
                      }
                    />
                  }
                  label="Other"
                />
              </Box>
            </Box>
          </Box>
          {/* Process Section checkboxes */}
          <Box display="flex" flexGrow={1}>
            <Box mr={4}>
              <Typography fontWeight="bold" lineHeight={2}>
                Process:
              </Typography>
              <Typography variant="body2">Check One</Typography>
            </Box>
            <Box
              display="grid"
              gridTemplateColumns="repeat(4, 1fr)"
              gap={2}
              flexGrow={1}
            >
              <Box display="flex" flexDirection="column">
                {/* First column checkboxes */}
                <FormControlLabel
                  control={
                    <Checkbox
                      sx={{
                        color: "darkgray", // Dark gray when unchecked
                        "&.Mui-checked": {
                          color: "#4F46E5", // Indigo (#4F46E5) when checked
                        },
                      }}
                      checked={formData.Process === "EstimatingSchedulingArt"}
                      onChange={(e) =>
                        handleInputChange(
                          "Process",
                          formData.Process === "EstimatingSchedulingArt"
                            ? ""
                            : "EstimatingSchedulingArt"
                        )
                      }
                    />
                  }
                  label=" Estimating, Scheduling, Art"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      sx={{
                        color: "darkgray", // Dark gray when unchecked
                        "&.Mui-checked": {
                          color: "#4F46E5", // Indigo (#4F46E5) when checked
                        },
                      }}
                      checked={formData.Process === "PrintingFinishing"}
                      onChange={(e) =>
                        handleInputChange(
                          "Process",
                          formData.Process === "PrintingFinishing"
                            ? ""
                            : "PrintingFinishing"
                        )
                      }
                    />
                  }
                  label="Printing & Finishing"
                />
              </Box>
              <Box display="flex" flexDirection="column">
                {/* Second column checkboxes */}
                <FormControlLabel
                  sx={{
                    "& .MuiFormControlLabel-label": { fontSize: "14px" },
                  }}
                  control={
                    <Checkbox
                      sx={{
                        color: "darkgray", // Dark gray when unchecked
                        "&.Mui-checked": {
                          color: "#4F46E5", // Indigo (#4F46E5) when checked
                        },
                      }}
                      checked={
                        formData.Process === "PurchasingReceivingShipping"
                      }
                      onChange={(e) =>
                        handleInputChange(
                          "Process",
                          formData.Process === "PurchasingReceivingShipping"
                            ? ""
                            : "PurchasingReceivingShipping"
                        )
                      }
                    />
                  }
                  label=" Purchasing, Receiving, Shipping"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      sx={{
                        color: "darkgray", // Dark gray when unchecked
                        "&.Mui-checked": {
                          color: "#4F46E5", // Indigo (#4F46E5) when checked
                        },
                      }}
                      checked={formData.Process === "Otherprocess"}
                      onChange={(e) =>
                        handleInputChange(
                          "Process",
                          formData.Process === "Otherprocess"
                            ? ""
                            : "Otherprocess"
                        )
                      }
                    />
                  }
                  label="Other"
                />
              </Box>
              <Box display="flex" flexDirection="column">
                {/* Third column checkboxes */}
                <FormControlLabel
                  control={
                    <Checkbox
                      sx={{
                        color: "darkgray", // Dark gray when unchecked
                        "&.Mui-checked": {
                          color: "#4F46E5", // Indigo (#4F46E5) when checked
                        },
                      }}
                      checked={formData.Process === "InstallationSource"}
                      onChange={(e) =>
                        handleInputChange(
                          "Process",
                          formData.Process === "InstallationSource"
                            ? ""
                            : "InstallationSource"
                        )
                      }
                    />
                  }
                  label="Installation"
                />
              </Box>
              <Box display="flex" flexDirection="column">
                {/* Fourth column checkboxes */}
              </Box>
            </Box>
          </Box>
          {/* Priority Section checkboxes */}
          <Box display="flex" flexGrow={1}>
            <Box mr={4}>
              <Typography fontWeight="bold" lineHeight={2}>
                Priority:
              </Typography>
              <Typography variant="body2">Check One</Typography>
            </Box>
            <Box
              display="grid"
              gridTemplateColumns="repeat(4, 1fr)"
              gap={2}
              flexGrow={1}
            >
              <Box display="flex" flexDirection="column">
                {/* First column checkboxes */}
                <FormControlLabel
                  control={
                    <Checkbox
                      sx={{
                        color: "darkgray", // Dark gray when unchecked
                        "&.Mui-checked": {
                          color: "#4F46E5", // Indigo (#4F46E5) when checked
                        },
                      }}
                      checked={formData.Priority === "LowPriority"}
                      onChange={(e) =>
                        handleInputChange(
                          "Priority",
                          formData.Priority === "LowPriority"
                            ? ""
                            : "LowPriority"
                        )
                      }
                    />
                  }
                  label="Low"
                />
              </Box>
              <Box display="flex" flexDirection="column">
                {/* Second column checkboxes */}

                <FormControlLabel
                  control={
                    <Checkbox
                      sx={{
                        color: "darkgray", // Dark gray when unchecked
                        "&.Mui-checked": {
                          color: "#4F46E5", // Indigo (#4F46E5) when checked
                        },
                      }}
                      checked={formData.Priority === "MediumPriority"}
                      onChange={(e) =>
                        handleInputChange(
                          "Priority",
                          formData.Priority === "MediumPriority"
                            ? ""
                            : "MediumPriority"
                        )
                      }
                    />
                  }
                  label="Medium"
                />
              </Box>
              <Box display="flex" flexDirection="column">
                {/* Third column checkboxes */}
                <FormControlLabel
                  control={
                    <Checkbox
                      sx={{
                        color: "darkgray", // Dark gray when unchecked
                        "&.Mui-checked": {
                          color: "#4F46E5", // Indigo (#4F46E5) when checked
                        },
                      }}
                      checked={formData.Priority === "HighPriority"}
                      onChange={(e) =>
                        handleInputChange(
                          "Priority",
                          formData.Priority === "HighPriority"
                            ? ""
                            : "HighPriority"
                        )
                      }
                    />
                  }
                  label="High"
                />
              </Box>
              <Box display="flex" flexDirection="column">
                {/* Fourth column checkboxes */}
                <FormControlLabel
                  control={
                    <Checkbox
                      sx={{
                        color: "darkgray", // Dark gray when unchecked
                        "&.Mui-checked": {
                          color: "#4F46E5", // Indigo (#4F46E5) when checked
                        },
                      }}
                      checked={formData.Priority === "UrgentCritical"}
                      onChange={(e) =>
                        handleInputChange(
                          "Priority",
                          formData.Priority === "UrgentCritical"
                            ? ""
                            : "UrgentCritical"
                        )
                      }
                    />
                  }
                  label="Urgent / Critical"
                />
              </Box>
            </Box>
          </Box>
          {/*  */}
          <TextField
            sx={{
              // Input text color - always black
              "& .MuiInputBase-input": {
                color: "#000000", // Black text color
              },
              // Border styling
              "& .MuiOutlinedInput-root": {
                // Default state - light gray
                "& fieldset": {
                  borderColor: "#d3d3d3", // Light gray as default
                },
                // Focus state
                "&.Mui-focused fieldset": {
                  borderColor: "#4F46E5", // Keep consistent with your desired color
                },
              },
              // Label color
              "& .MuiInputLabel-root.Mui-focused": {
                color: "#4F46E5", // Changes label color to match when focused
              },
            }}
            label="Describe the issue or problem in detail"
            fullWidth
            size="small"
            multiline
            minRows={3}
            value={formData.Describe_the_issue_or_problem_in_detail || ""}
            onChange={(e) =>
              handleInputChange(
                "Describe_the_issue_or_problem_in_detail",
                e.target.value
              )
            }
          />
          <Box bgcolor="black" p={2} borderRadius={1}>
            <Typography fontWeight="bold" color="white">
              Stop here. Submit CAR / PAR to Mark Woster, Manager
            </Typography>
          </Box>
          <Box display="grid" gridTemplateColumns="repeat(3, 1fr)" gap={2}>
            <TextField
              sx={{
                // Input text color - always black
                "& .MuiInputBase-input": {
                  color: "#000000", // Black text color
                },
                // Border styling
                "& .MuiOutlinedInput-root": {
                  // Default state - light gray
                  "& fieldset": {
                    borderColor: "#d3d3d3", // Light gray as default
                  },
                  // Focus state
                  "&.Mui-focused fieldset": {
                    borderColor: "#4F46E5", // Keep consistent with your desired color
                  },
                },
                // Label color
                "& .MuiInputLabel-root.Mui-focused": {
                  color: "#4F46E5", // Changes label color to match when focused
                },
              }}
              label="Assigned To"
              fullWidth
              size="small"
              value={formData.assignedTo || ""}
              onChange={(e) => handleInputChange("assignedTo", e.target.value)}
            />
            <TextField
              sx={{
                // Input text color - always black
                "& .MuiInputBase-input": {
                  color: "#000000", // Black text color
                },
                // Border styling
                "& .MuiOutlinedInput-root": {
                  // Default state - light gray
                  "& fieldset": {
                    borderColor: "#d3d3d3", // Light gray as default
                  },
                  // Focus state
                  "&.Mui-focused fieldset": {
                    borderColor: "#4F46E5", // Keep consistent with your desired color
                  },
                },
                // Label color
                "& .MuiInputLabel-root.Mui-focused": {
                  color: "#4F46E5", // Changes label color to match when focused
                },
              }}
              type="date"
              label="Date Assigned"
              fullWidth
              size="small"
              InputLabelProps={{ shrink: true }}
              value={formData.AssignedDate || ""}
              onChange={(e) =>
                handleInputChange("AssignedDate", e.target.value)
              }
            />
            <TextField
              sx={{
                // Input text color - always black
                "& .MuiInputBase-input": {
                  color: "#000000", // Black text color
                },
                // Border styling
                "& .MuiOutlinedInput-root": {
                  // Default state - light gray
                  "& fieldset": {
                    borderColor: "#d3d3d3", // Light gray as default
                  },
                  // Focus state
                  "&.Mui-focused fieldset": {
                    borderColor: "#4F46E5", // Keep consistent with your desired color
                  },
                },
                // Label color
                "& .MuiInputLabel-root.Mui-focused": {
                  color: "#4F46E5", // Changes label color to match when focused
                },
              }}
              type="date"
              label="Respond By"
              fullWidth
              size="small"
              InputLabelProps={{ shrink: true }}
              value={formData.respondBy || ""}
              onChange={(e) => handleInputChange("respondBy", e.target.value)}
            />
          </Box>
          <Box bgcolor="black" p={2} borderRadius={1}>
            <Typography fontWeight="bold" color="white">
              To be completed by the Assignee
            </Typography>
          </Box>
          <TextField
            sx={{
              // Input text color - always black
              "& .MuiInputBase-input": {
                color: "#000000", // Black text color
              },
              // Border styling
              "& .MuiOutlinedInput-root": {
                // Default state - light gray
                "& fieldset": {
                  borderColor: "#d3d3d3", // Light gray as default
                },
                // Focus state
                "&.Mui-focused fieldset": {
                  borderColor: "#4F46E5", // Keep consistent with your desired color
                },
              },
              // Label color
              "& .MuiInputLabel-root.Mui-focused": {
                color: "#4F46E5", // Changes label color to match when focused
              },
            }}
            label="Containment / Immediate Corrections: (Required only for corrective action requests.)"
            fullWidth
            size="small"
            multiline
            minRows={2}
            value={formData.containment || ""}
            onChange={(e) => handleInputChange("containment", e.target.value)}
          />
          <TextField
            sx={{
              // Input text color - always black
              "& .MuiInputBase-input": {
                color: "#000000", // Black text color
              },
              // Border styling
              "& .MuiOutlinedInput-root": {
                // Default state - light gray
                "& fieldset": {
                  borderColor: "#d3d3d3", // Light gray as default
                },
                // Focus state
                "&.Mui-focused fieldset": {
                  borderColor: "#4F46E5", // Keep consistent with your desired color
                },
              },
              // Label color
              "& .MuiInputLabel-root.Mui-focused": {
                color: "#4F46E5", // Changes label color to match when focused
              },
            }}
            label="Root Cause of Problem:  (Required for all corrective and preventive issues; optional for suggestions for improvement.)"
            fullWidth
            size="small"
            multiline
            minRows={2}
            value={formData.rootCause || ""}
            onChange={(e) => handleInputChange("rootCause", e.target.value)}
          />
          <TextField
            sx={{
              // Input text color - always black
              "& .MuiInputBase-input": {
                color: "#000000", // Black text color
              },
              // Border styling
              "& .MuiOutlinedInput-root": {
                // Default state - light gray
                "& fieldset": {
                  borderColor: "#d3d3d3", // Light gray as default
                },
                // Focus state
                "&.Mui-focused fieldset": {
                  borderColor: "#4F46E5", // Keep consistent with your desired color
                },
              },
              // Label color
              "& .MuiInputLabel-root.Mui-focused": {
                color: "#4F46E5", // Changes label color to match when focused
              },
            }}
            label="Action Plan:  (Actions taken to eliminate root cause, resolve issue and prevent recurrence.)"
            fullWidth
            size="small"
            multiline
            minRows={2}
            value={formData.actionPlan || ""}
            onChange={(e) => handleInputChange("actionPlan", e.target.value)}
          />
          <Box display="grid" gridTemplateColumns="repeat(2, 1fr)" gap={2}>
            <TextField
              sx={{
                // Input text color - always black
                "& .MuiInputBase-input": {
                  color: "#000000", // Black text color
                },
                // Border styling
                "& .MuiOutlinedInput-root": {
                  // Default state - light gray
                  "& fieldset": {
                    borderColor: "#d3d3d3", // Light gray as default
                  },
                  // Focus state
                  "&.Mui-focused fieldset": {
                    borderColor: "#4F46E5", // Keep consistent with your desired color
                  },
                },
                // Label color
                "& .MuiInputLabel-root.Mui-focused": {
                  color: "#4F46E5", // Changes label color to match when focused
                },
              }}
              type="date"
              label="Date Action Completed"
              fullWidth
              size="small"
              InputLabelProps={{ shrink: true }}
              value={formData.actionCompleted || ""}
              onChange={(e) =>
                handleInputChange("actionCompleted", e.target.value)
              }
            />
            <TextField
              sx={{
                // Input text color - always black
                "& .MuiInputBase-input": {
                  color: "#000000", // Black text color
                },
                // Border styling
                "& .MuiOutlinedInput-root": {
                  // Default state - light gray
                  "& fieldset": {
                    borderColor: "#d3d3d3", // Light gray as default
                  },
                  // Focus state
                  "&.Mui-focused fieldset": {
                    borderColor: "#4F46E5", // Keep consistent with your desired color
                  },
                },
                // Label color
                "& .MuiInputLabel-root.Mui-focused": {
                  color: "#4F46E5", // Changes label color to match when focused
                },
              }}
              label="Assignee Name"
              fullWidth
              size="small"
              value={formData.Assignee_Name || ""}
              onChange={(e) =>
                handleInputChange("Assignee_Name", e.target.value)
              }
            />
          </Box>
          <Box bgcolor="black" p={2} borderRadius={1}>
            <Typography fontWeight="bold" color="white">
              Stop here. Return CAR / PAR to Mark Woster, Manager
            </Typography>
          </Box>
          <Box display="grid" gridTemplateColumns="repeat(2, 1fr)" gap={2}>
            <FormControl
              sx={{
                // Input text color - always black
                "& .MuiInputBase-input": {
                  color: "#000000", // Black text color
                },
                // Border styling
                "& .MuiOutlinedInput-root": {
                  // Default state - light gray
                  "& fieldset": {
                    borderColor: "#d3d3d3", // Light gray as default
                  },
                  // Focus state
                  "&.Mui-focused fieldset": {
                    borderColor: "#4F46E5", // Keep consistent with your desired color
                  },
                },
                // Label color
                "& .MuiInputLabel-root.Mui-focused": {
                  color: "#4F46E5", // Changes label color to match when focused
                },
              }}
              fullWidth
              size="small"
              error={!!error.qualityOwner1}
            >
              <InputLabel>Verified By</InputLabel>
              <Select
                value={qualityOwner1 || ""}
                onChange={(e) => {
                  const value = e.target.value;
                  console.log("Selected Verified owner ID:", value);
                  setQualityOwner1(value);
                  handleInputChange("qualityOwner1", value);
                }}
                label="Verified By"
              >
                <MenuItem value="" disabled>
                  Select User
                </MenuItem>
                {employees && employees.length > 0 ? (
                  employees.map((employee) => (
                    <MenuItem key={employee.ID} value={employee.ID}>
                      {employee.Name.zc_display_value}
                    </MenuItem>
                  ))
                ) : (
                  <MenuItem value="" disabled>
                    No User Found
                  </MenuItem>
                )}
              </Select>
              {error.qualityOwner1 && (
                <Typography variant="caption" color="error">
                  {error.qualityOwner1}
                </Typography>
              )}
            </FormControl>

            <TextField
              sx={{
                // Input text color - always black
                "& .MuiInputBase-input": {
                  color: "#000000", // Black text color
                },
                // Border styling
                "& .MuiOutlinedInput-root": {
                  // Default state - light gray
                  "& fieldset": {
                    borderColor: "#d3d3d3", // Light gray as default
                  },
                  // Focus state
                  "&.Mui-focused fieldset": {
                    borderColor: "#4F46E5", // Keep consistent with your desired color
                  },
                },
                // Label color
                "& .MuiInputLabel-root.Mui-focused": {
                  color: "#4F46E5", // Changes label color to match when focused
                },
              }}
              type="date"
              label="Verified Date"
              fullWidth
              size="small"
              InputLabelProps={{ shrink: true }}
              value={formData.verifiedDate || ""}
              onChange={(e) =>
                handleInputChange("verifiedDate", e.target.value)
              }
            />
          </Box>
          <Box display="flex" alignItems="flex-start" gap={1}>
            {/* Label Section */}
            <Box minWidth="135px">
              <Typography fontWeight="bold" lineHeight={3}>
                Result:
              </Typography>
            </Box>
            {/* Checkbox Grid */}
            <Box display="flex" flexDirection="column" gap={0.5}>
              <Box display="flex" flexWrap="wrap" gap={1}>
                <FormControlLabel
                  sx={{ m: 0 }}
                  control={
                    <Checkbox
                      sx={{
                        color: "darkgray", // Dark gray when unchecked
                        "&.Mui-checked": {
                          color: "#4F46E5", // Indigo (#4F46E5) when checked
                        },
                      }}
                      checked={formData.Result === "PassResult"}
                      onChange={(e) =>
                        handleInputChange(
                          "Result",
                          formData.Result === "PassResult" ? "" : "PassResult"
                        )
                      }
                    />
                  }
                  label="Pass"
                />
                <FormControlLabel
                  sx={{ m: 0 }}
                  control={
                    <Checkbox
                      sx={{
                        color: "darkgray", // Dark gray when unchecked
                        "&.Mui-checked": {
                          color: "#4F46E5", // Indigo (#4F46E5) when checked
                        },
                      }}
                      checked={formData.Result === "FailResult"}
                      onChange={(e) =>
                        handleInputChange(
                          "Result",
                          formData.Result === "FailResult" ? "" : "FailResult"
                        )
                      }
                    />
                  }
                  label="Fail: returned for further processing"
                />
              </Box>
            </Box>
          </Box>
          <TextField
            sx={{
              // Input text color - always black
              "& .MuiInputBase-input": {
                color: "#000000", // Black text color
              },
              // Border styling
              "& .MuiOutlinedInput-root": {
                // Default state - light gray
                "& fieldset": {
                  borderColor: "#d3d3d3", // Light gray as default
                },
                // Focus state
                "&.Mui-focused fieldset": {
                  borderColor: "#4F46E5", // Keep consistent with your desired color
                },
              },
              // Label color
              "& .MuiInputLabel-root.Mui-focused": {
                color: "#4F46E5", // Changes label color to match when focused
              },
            }}
            label="Notes / Comments"
            fullWidth
            size="small"
            multiline
            minRows={2}
            value={formData.notes || ""}
            onChange={(e) => handleInputChange("notes", e.target.value)}
          />
        </Box>
        <Box sx={{ pt: 5, pb: 2, justifyContent: "left" }}>
          <GlobalButton
            sx={{ mr: 1 }}
            icon={<SaveIcon />}
            onClick={handleSubmit}
            disabled={submitting} // Disable button while submitting
          >
            {submitting ? "Saving..." : "Save"}
          </GlobalButton>
          <GlobalButton
            icon={<RestartAltIcon />}
            onClick={onClose}
            variant="outlined"
          >
            Reset
          </GlobalButton>
        </Box>
      </DialogContent>
      {/* Add this right before the closing </Dialog> tag */}
      {isSubmitting && <Loader message="" />}
    </Dialog>
  );
}
